'use client';

import { ReactElement, useState } from 'react';
import clsx from 'clsx';
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuItem as UIMenuItem,
} from '@headlessui/react';

import { Link } from '@northladder/i18n';

export interface IMenuDropdownItem {
  /**
   * The href attribute for the menu item's link.
   */
  href: string;
  /**
   * An optional ReactElement to be displayed as an icon for the menu item.
   */
  icon: ReactElement;
  /**
   * The label of the menu item.
   */
  label: string;
}

interface IMenuDropdownOptions {
  /**
   * An array of MenuItem objects representing the items in the dropdown menu.
   */
  items: IMenuDropdownItem[];
}

/**
 * Properties for a single menu dropdown item.
 */
interface IMenuDropdownItemProps {
  /** The menu item data to render. */
  item: IMenuDropdownItem;
  /** Callback function triggered when the menu item is clicked. */
  onClick: (item: IMenuDropdownItem) => void;
}

/**
 * -----------------------------------------------------------------------------
 * A private functional Component that renders a single menu item.
 * TODO: Revise styling issue in file
 */
function MenuItem({ item, onClick }: Readonly<IMenuDropdownItemProps>) {
  const { href, label, icon } = item;

  const handleClick = () => {
    onClick(item);
  };

  return (
    <UIMenuItem key={label}>
      <Link
        className="data-focus:bg-blue-100/45 flex gap-2 rounded-lg px-3 py-2"
        href={href}
        onClick={handleClick}
      >
        {icon}
        {label}
      </Link>
    </UIMenuItem>
  );
}

/**
 * Properties for the MenuDropdown component.
 */
interface IMenuDropdownProps extends IMenuDropdownOptions {
  /** Whether to use a dark theme for the dropdown. */
  dark?: boolean;
}

/**
 * -----------------------------------------------------------------------------
 * - This renders a dropdown menu with the provided menu items.
 */
export function MenuDropdown({ items, dark = false }: IMenuDropdownProps) {
  const [activeItem, setActiveItem] = useState<IMenuDropdownItem | undefined>(
    items[0]
  );

  if (!activeItem) {
    return null;
  }

  const { label, icon } = activeItem;

  const handleSetMenuItem = (item: IMenuDropdownItem) => {
    setActiveItem(item);
  };

  return (
    <Menu>
      <MenuButton className="flex items-center gap-3 px-2 py-2.5">
        {icon}
        <span
          className={`
            text-base font-semibold underline underline-offset-4 max-sm:hidden
            ${dark ? 'text-black' : 'text-white'}
          `}
        >
          {label}
        </span>
      </MenuButton>

      {items ? (
        <MenuItems
          anchor="bottom end"
          className={clsx(
            'flex w-52 origin-top-right flex-col gap-4 rounded-xl border',
            'px-5 py-4 text-base font-semibold opacity-100 transition duration-100',
            'ease-out [--anchor-gap:var(--spacing-1)] focus:outline-none',
            'data-[closed]:scale-95 data-[closed]:opacity-0',
            dark
              ? 'border-black/5 bg-black/40 text-black'
              : 'border-white/5 bg-white/40 text-white'
          )}
        >
          {items.map((item) => (
            <MenuItem
              key={item.label}
              item={item}
              onClick={handleSetMenuItem}
            />
          ))}
        </MenuItems>
      ) : null}
    </Menu>
  );
}
